import { resetAnalytics } from "../analytics/analytics";
import { isMSquaredHub } from "../utils/isEnv";

import { postAuthMessage, postLogoutMessage } from "./utils/broadcastMessages";
import {
  AuthProviderAuth0,
  forceRefreshTokensAuth0,
  getIdTokenAuth0,
  isAuthenticatedAuth0,
  signOutAuth0,
} from "./auth0";
import {
  AuthProviderAws,
  forceRefreshTokensAws,
  getIdTokenAws,
  isAuthenticatedAws,
  signOutAws,
} from "./aws";
import {
  AuthProviderAzure,
  forceRefreshTokensAzure,
  getIdTokenAzure,
  isAuthenticatedAzure,
  signOutAzure,
} from "./azure";
import {
  AuthProviderCustomAuth,
  forceRefreshTokensCustomAuth,
  getIdTokenCustomAuth,
  isAuthenticatedCustomAuth,
  signoutCustomAuth,
} from "./custom_auth";
import {
  AuthProviderMSquared,
  getIdTokenMSquared,
  isAuthenticatedMSquared,
  signoutMsquared,
} from "./msquared";

const authProvider = import.meta.env.VITE_AUTH_PROVIDER;
const isAuthDisabled =
  import.meta.env.VITE_DISABLE_AUTH === "true" || import.meta.env.VITEST;

const isAuthenticated = async () => {
  if (isAuthDisabled) {
    return true;
  }

  let isAuthenticatedHandler: () => Promise<boolean>;
  if (isMSquaredHub()) {
    isAuthenticatedHandler = isAuthenticatedMSquared;
  } else if (authProvider === "aws") {
    isAuthenticatedHandler = isAuthenticatedAws;
  } else if (authProvider === "azure") {
    isAuthenticatedHandler = isAuthenticatedAzure;
  } else if (authProvider === "auth0") {
    isAuthenticatedHandler = isAuthenticatedAuth0;
  } else if (authProvider === "custom_auth") {
    isAuthenticatedHandler = isAuthenticatedCustomAuth;
  } else {
    throw new Error("Cloud provider not supported");
  }

  const isAuthenticated = await isAuthenticatedHandler();
  postAuthMessage(isAuthenticated);
  return isAuthenticated;
};

const getIdToken = async () => {
  if (isAuthDisabled) {
    return "test_token";
  }

  if (isMSquaredHub()) {
    return await getIdTokenMSquared();
  } else if (authProvider === "aws") {
    return await getIdTokenAws();
  } else if (authProvider === "azure") {
    return await getIdTokenAzure();
  } else if (authProvider === "auth0") {
    return await getIdTokenAuth0();
  } else if (authProvider === "custom_auth") {
    return await getIdTokenCustomAuth();
  }
  throw new Error("Cloud provider not supported");
};

const signOut = async () => {
  if (isAuthDisabled) {
    return;
  }

  resetAnalytics();

  if (isMSquaredHub()) {
    await signoutMsquared();
    window.location.href = window.location.origin;
    return;
  } else if (authProvider === "aws") {
    await signOutAws();
    postLogoutMessage();
    window.localStorage.clear();
    window.location.reload();
  } else if (authProvider === "azure") {
    postLogoutMessage();
    await signOutAzure();
  } else if (authProvider === "auth0") {
    await signOutAuth0();
    postLogoutMessage();
    window.localStorage.clear();
    window.location.reload();
  } else if (authProvider === "custom_auth") {
    await signoutCustomAuth();
    // postLogoutMessage();
    // window.localStorage.clear();
    window.sessionStorage.clear();
    window.location.href = import.meta.env.VITE_MAIN_URL || "/";
  } else {
    throw new Error("Cloud provider not supported");
  }
};

const forceRefreshTokens = async () => {
  if (isAuthDisabled) {
    return;
  }

  if (isMSquaredHub()) {
    return;
  } else if (authProvider === "aws") {
    return await forceRefreshTokensAws();
  } else if (authProvider === "azure") {
    return await forceRefreshTokensAzure();
  } else if (authProvider === "auth0") {
    return await forceRefreshTokensAuth0();
  } else if (authProvider === "custom_auth") {
    return await forceRefreshTokensCustomAuth();
  }
  throw new Error("Cloud provider not supported");
};

const AuthProvider: React.FunctionComponent<{ children: React.ReactNode }> = ({
  children,
}) => {
  if (isAuthDisabled) {
    return <>{children}</>;
  }

  if (isMSquaredHub()) {
    return <AuthProviderMSquared>{children}</AuthProviderMSquared>;
  } else if (authProvider === "aws") {
    return <AuthProviderAws>{children}</AuthProviderAws>;
  } else if (authProvider === "azure") {
    return <AuthProviderAzure>{children}</AuthProviderAzure>;
  } else if (authProvider === "auth0") {
    return <AuthProviderAuth0>{children}</AuthProviderAuth0>;
  } else if (authProvider === "custom_auth") {
    return <AuthProviderCustomAuth>{children}</AuthProviderCustomAuth>;
  }
  throw new Error("Cloud provider not supported");
};

export {
  AuthProvider,
  forceRefreshTokens,
  getIdToken,
  isAuthenticated,
  signOut,
};
